import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Send, Loader2, CalendarCheck, MessageSquarePlus, Phone, MapPin, Clock, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { t } from "@/lib/i18n";
import { BUSINESS } from "@/lib/business";
import LanguageToggle from "@/components/gentry/LanguageToggle";
import MessageBubble from "@/components/gentry/MessageBubble";
import LeadForm from "@/components/gentry/LeadForm";
import AppointmentPicker from "@/components/gentry/AppointmentPicker";

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function ChatWidget() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [langMode, setLangMode] = useState("auto"); // "auto" | "en" | "ht"
  const [uiLang, setUiLang] = useState("en"); // language for UI labels; reflects last detected
  const [leadOpen, setLeadOpen] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const listRef = useRef(null);

  // Initialize session
  useEffect(() => {
    api
      .post("/chat/session")
      .then(({ data }) => {
        setSessionId(data.session_id);
        setMessages([
          {
            id: "greeting",
            role: "assistant",
            content: t("en", "greeting"),
            language: "en",
            created_at: new Date().toISOString(),
          },
        ]);
      })
      .catch(() => toast.error("Could not start chat. Please refresh."));
  }, []);

  // Autoscroll
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, sending, transcribing]);

  const forceLangForApi = () => (langMode === "auto" ? null : langMode);

  const applyAssistantResult = (assistantText, lang, action, userLang) => {
    if (langMode === "auto" && userLang) setUiLang(userLang);
    if (langMode !== "auto") setUiLang(langMode);
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: "assistant",
        content: assistantText,
        language: lang,
        action,
        created_at: new Date().toISOString(),
      },
    ]);
    if (action === "collect_message") setTimeout(() => setLeadOpen(true), 350);
    if (action === "schedule_appointment") setTimeout(() => setApptOpen(true), 350);
  };

  const sendText = async (text) => {
    if (!text.trim() || !sessionId) return;
    const userLangGuess =
      langMode !== "auto" ? langMode : /[èòàáâçùûñ]/i.test(text) ? "ht" : uiLang;
    const userMsg = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
      language: userLangGuess,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/chat/message", {
        session_id: sessionId,
        text,
        force_language: forceLangForApi(),
      });
      applyAssistantResult(data.reply, data.lang, data.action, data.user_lang);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error("Mic not supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        await handleAudioBlob(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Could not access microphone.");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    setRecording(false);
  };

  const handleAudioBlob = async (blob) => {
    if (!sessionId) return;
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append("session_id", sessionId);
      form.append("file", blob, "voice.webm");
      const force = forceLangForApi();
      if (force) form.append("force_language", force);
      const { data } = await api.post("/chat/voice", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Push transcript as user message
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "user",
          content: data.transcript,
          language: data.user_lang,
          created_at: new Date().toISOString(),
        },
      ]);
      applyAssistantResult(data.reply, data.lang, data.action, data.user_lang);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setTranscribing(false);
    }
  };

  const quickPrompts = uiLang === "ht"
    ? [
        { icon: Clock, label: "Lè travay yo", value: "Ki lè nou ouvè?" },
        { icon: MapPin, label: "Kote", value: "Kote biwo a ye?" },
        { icon: Phone, label: "Sèvis", value: "Ki sèvis nou ofri?" },
      ]
    : [
        { icon: Clock, label: "Hours", value: "What are your business hours?" },
        { icon: MapPin, label: "Location", value: "Where are you located?" },
        { icon: Phone, label: "Services", value: "What services do you offer?" },
      ];

  return (
    <>
      <div
        className="relative w-full max-w-2xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden border border-[#EBE4D3]"
        data-testid="chat-widget"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 bg-[#1E3B2F] text-white">
          <div className="w-11 h-11 rounded-full overflow-hidden ring-2 ring-white/20 shrink-0">
            <img
              src="https://images.pexels.com/photos/3771087/pexels-photo-3771087.jpeg"
              alt="Gentry avatar"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-serif-display text-xl leading-none">Gentry</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-xs text-white/80">{t(uiLang, "online")}</span>
            </div>
          </div>
          <a
            href={`tel:${BUSINESS.phoneTel}`}
            className="hidden sm:inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#C25934] hover:bg-[#a44827] text-white text-xs font-medium btn-lift"
            data-testid="chat-header-call-button"
            aria-label={`${t(uiLang, "callUs")} ${BUSINESS.phone}`}
          >
            <PhoneCall size={13} />
            {t(uiLang, "callUs")}
          </a>
          <a
            href={`tel:${BUSINESS.phoneTel}`}
            className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#C25934] hover:bg-[#a44827] text-white"
            data-testid="chat-header-call-button-mobile"
            aria-label={`${t(uiLang, "callUs")} ${BUSINESS.phone}`}
          >
            <PhoneCall size={15} />
          </a>
          <LanguageToggle
            mode={langMode}
            onChange={(v) => {
              setLangMode(v);
              if (v !== "auto") setUiLang(v);
            }}
            uiLang={uiLang}
          />
        </div>

        {/* Message list */}
        <div
          ref={listRef}
          className="chat-scroll bg-[#F9F6F0] px-4 py-6 space-y-4 overflow-y-auto"
          style={{ height: "min(60vh, 480px)" }}
          data-testid="chat-messages-list"
        >
          {messages.map((m) => (
            <MessageBubble
              key={m.id}
              role={m.role}
              content={m.content}
              language={m.language}
              time={formatTime(m.created_at)}
            />
          ))}
          <AnimatePresence>
            {(sending || transcribing) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2 text-xs text-[#6B7280] px-1"
                data-testid="chat-typing-indicator"
              >
                <div className="flex gap-1">
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[#1E3B2F]" />
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[#1E3B2F]" />
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[#1E3B2F]" />
                </div>
                <span>
                  {transcribing ? t(uiLang, "transcribing") : t(uiLang, "typing")}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Quick actions */}
        <div className="px-4 pt-3 flex flex-wrap gap-2 bg-white border-t border-[#EBE4D3]">
          {quickPrompts.map((q) => (
            <button
              key={q.label}
              onClick={() => sendText(q.value)}
              className="text-xs px-3 py-1.5 rounded-full border border-[#EBE4D3] hover:border-[#1E3B2F] hover:text-[#1E3B2F] flex items-center gap-1.5 transition-colors"
              data-testid={`quick-prompt-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <q.icon size={12} />
              {q.label}
            </button>
          ))}
          <button
            onClick={() => setLeadOpen(true)}
            className="text-xs px-3 py-1.5 rounded-full bg-[#F2ECDF] hover:bg-[#EBE4D3] flex items-center gap-1.5 transition-colors"
            data-testid="quick-leave-message-button"
          >
            <MessageSquarePlus size={12} />
            {t(uiLang, "quickMessage")}
          </button>
          <button
            onClick={() => setApptOpen(true)}
            className="text-xs px-3 py-1.5 rounded-full bg-[#C25934] text-white hover:bg-[#a44827] flex items-center gap-1.5 transition-colors"
            data-testid="quick-schedule-button"
          >
            <CalendarCheck size={12} />
            {t(uiLang, "quickAppointment")}
          </button>
        </div>

        {/* Input */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!sending) sendText(input);
          }}
          className="flex items-center gap-2 p-4 bg-white"
        >
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            className={
              "w-11 h-11 rounded-full shrink-0 flex items-center justify-center transition-colors " +
              (recording
                ? "bg-[#C25934] text-white mic-recording"
                : "bg-[#F2ECDF] text-[#1E3B2F] hover:bg-[#EBE4D3]")
            }
            aria-label={recording ? t(uiLang, "micStop") : t(uiLang, "micStart")}
            data-testid="voice-record-button"
            disabled={transcribing}
          >
            <Mic size={18} />
          </button>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={recording ? t(uiLang, "listening") : t(uiLang, "placeholder")}
            className="h-11 bg-[#F9F6F0] border-[#EBE4D3] focus-visible:ring-[#C25934]"
            data-testid="chat-input-field"
            disabled={recording || transcribing}
          />
          <Button
            type="submit"
            disabled={!input.trim() || sending}
            className="h-11 px-4 bg-[#1E3B2F] hover:bg-[#294d3f] text-white btn-lift"
            data-testid="chat-send-button"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </Button>
        </form>
      </div>

      <LeadForm
        open={leadOpen}
        onOpenChange={setLeadOpen}
        lang={uiLang}
        sessionId={sessionId}
      />
      <AppointmentPicker
        open={apptOpen}
        onOpenChange={setApptOpen}
        lang={uiLang}
        sessionId={sessionId}
      />
    </>
  );
}
